import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Loader2, TrendingUp, Users, AlertTriangle, Server, Lock, Zap, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { seguridadAPI } from './api';
import { toast } from 'sonner';

const RANGES = [
  { key: '24h', label: '24 horas' },
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
];

const COLOR_INTENTOS = '#9d8272';
const COLOR_FALLOS = '#b85555';

const KPI = ({ icon: Icon, label, value, tone = 'default' }) => {
  const toneMap = {
    default: 'text-[#2D2D2D]',
    warning: 'text-[#c0855a]',
    danger: 'text-[#b85555]',
  };
  return (
    <div className="bg-white border border-[#E8E8E7] rounded-lg p-4">
      <div className="flex items-center gap-2 text-xs text-[#5A5A5A]">
        <Icon size={13} />
        <span>{label}</span>
      </div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${toneMap[tone]}`}>
        {value}
      </div>
    </div>
  );
};

const DashboardTab = () => {
  const [range, setRange] = useState('24h');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    seguridadAPI
      .dashboard(range)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Error al cargar el dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const handlePurge = () => {
    if (!window.confirm('¿Purgar registros de login_attempts con más de 30 días?')) return;
    seguridadAPI
      .purge(30)
      .then((r) => toast.success(`Purgados ${r.data.deleted} registros`))
      .catch(() => toast.error('Error al purgar'));
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin text-[#9d8272]" />
      </div>
    );
  }

  const kpis = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {/* Range selector + Purga */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 border border-[#E8E8E7] rounded-lg p-1 bg-white">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded ${
                range === r.key ? 'bg-[#9d8272] text-white' : 'text-[#5A5A5A] hover:text-[#2D2D2D]'
              }`}
              data-testid={`range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePurge}
          className="text-xs"
          data-testid="btn-purge"
        >
          <Trash2 size={12} className="mr-1.5" />
          Purgar &gt; 30 días
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI icon={TrendingUp} label="Intentos (60m)" value={kpis.intentos_60min ?? 0} />
        <KPI icon={AlertTriangle} label="Fallos (60m)" value={kpis.fallos_60min ?? 0} tone="warning" />
        <KPI icon={Server} label="IPs activas (60m)" value={kpis.ips_activas_60min ?? 0} />
        <KPI icon={Users} label="Emails con fallos (60m)" value={kpis.emails_fallidos_60min ?? 0} tone="warning" />
        <KPI icon={Lock} label="Bloqueos activos" value={kpis.bloqueos_activos ?? 0} tone="danger" />
        <KPI icon={Zap} label="Spraying sin ack" value={kpis.eventos_spraying_activos ?? 0} tone="danger" />
      </div>

      {/* Serie de intentos vs fallos */}
      <div className="bg-white border border-[#E8E8E7] rounded-lg p-4">
        <div className="text-sm font-semibold text-[#2D2D2D] mb-3">
          Intentos vs Fallos ({data.granularity === 'hour' ? 'por hora' : 'por día'})
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.series || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
            <XAxis
              dataKey="ts"
              tick={{ fontSize: 10, fill: '#888888' }}
              tickFormatter={(v) => {
                const d = new Date(v);
                if (data.granularity === 'hour')
                  return `${d.getHours().toString().padStart(2, '0')}:00`;
                return `${d.getDate()}/${d.getMonth() + 1}`;
              }}
            />
            <YAxis tick={{ fontSize: 10, fill: '#888888' }} />
            <Tooltip
              labelFormatter={(v) => new Date(v).toLocaleString('es-PE')}
              contentStyle={{ fontSize: 11 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="intentos" stroke={COLOR_INTENTOS} strokeWidth={2} dot={false} name="Intentos" />
            <Line type="monotone" dataKey="fallos" stroke={COLOR_FALLOS} strokeWidth={2} dot={false} name="Fallos" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Top emails y Top IPs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E8E8E7] rounded-lg p-4">
          <div className="text-sm font-semibold text-[#2D2D2D] mb-3">
            Top 10 emails con más fallos
          </div>
          {(data.top_emails || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-xs text-[#888888]">
              Sin fallos en este rango
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.top_emails} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#888888' }} />
                <YAxis
                  type="category"
                  dataKey="email"
                  tick={{ fontSize: 10, fill: '#5A5A5A' }}
                  width={140}
                />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="fallos" fill={COLOR_FALLOS} name="Fallos" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E8E8E7] rounded-lg p-4">
          <div className="text-sm font-semibold text-[#2D2D2D] mb-3">
            Top 10 IPs con más fallos
          </div>
          {(data.top_ips || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-xs text-[#888888]">
              Sin fallos en este rango
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.top_ips} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#888888' }} />
                <YAxis
                  type="category"
                  dataKey="ip"
                  tick={{ fontSize: 10, fill: '#5A5A5A' }}
                  width={130}
                />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="fallos" fill={COLOR_INTENTOS} name="Fallos" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Timeline spraying */}
      <div className="bg-white border border-[#E8E8E7] rounded-lg p-4">
        <div className="text-sm font-semibold text-[#2D2D2D] mb-3">
          Eventos de password spraying
        </div>
        {(data.spraying || []).length === 0 ? (
          <div className="h-16 flex items-center justify-center text-xs text-[#888888]">
            Sin eventos en este rango
          </div>
        ) : (
          <div className="divide-y divide-[#E8E8E7]">
            {data.spraying.slice(0, 20).map((e) => {
              const toneColor =
                e.severity === 'block_long'
                  ? 'text-red-600'
                  : e.severity === 'block_short'
                  ? 'text-amber-600'
                  : 'text-yellow-600';
              return (
                <div
                  key={e.id}
                  className="py-2 flex items-center gap-3 text-xs"
                  data-testid={`spraying-${e.id}`}
                >
                  <AlertTriangle size={12} className={toneColor} />
                  <span className="font-mono text-[#2D2D2D]">{e.ip_address}</span>
                  <span className="text-[#888888]">
                    {e.emails_count} emails distintos
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${toneColor}`}>
                    {e.severity}
                  </span>
                  {e.trusted_ip && (
                    <span className="text-[10px] text-[#5A5A5A] bg-[#fbf7f0] px-1.5 py-0.5 rounded">
                      IP confiable
                    </span>
                  )}
                  <span className="ml-auto text-[#888888]">
                    {new Date(e.detected_at).toLocaleString('es-PE')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTab;
